@if (isLoading) {
  <div class="xi-loader-container__center">
    <xi-animation [loop]="true" animation="cx_loader"></xi-animation>
  </div>
}

<ng-container *transloco="let t">
  <div class="widget-container">
    <div class="color-picker-container">
      <div class="widget-chart__color-picker">
        <xi-color-picker
          #widgetChartColorPicker
          [direction]="6"
          [preselectedColors]="projectColors"
          [selectedColor]="selectedItemColor"
          (onColorChange)="itemColorChange$.next($event.detail.color)"
        ></xi-color-picker>
      </div>
    </div>
    <div
      class="context-menu-trigger-container"
      [ngClass]="{
        hide:
          isShowAsDataTable ||
          isCustomSortMode ||
          noData ||
          hasInvalidComparison ||
          noPermission ||
          noDocuments,
        'sort-container': canSort,
      }"
    >
      @if (canSort) {
        <div class="chart-sort-container--y">
          <xi-icon-button
            buttonSize="small"
            [disabled]="isSortDisabled()"
            (onClick)="sortChartItems(verticalAxisField)"
            [icon]="getSortIcon(verticalAxisField)"
            [tooltip]="{ text: verticalAxisSortTooltip, direction: 2 }"
          >
          </xi-icon-button>
        </div>
      }

      <!-- The highchart appears here -->
      <div
        #elementContent
        class="content"
        [ngClass]="{
          hide: isChartHidden,
          'content--journey-touchpoints': isJourneyTouchpointsChart,
        }"
      ></div>

      @if (!dashboard.locked) {
        <div
          [matMenuTriggerFor]="widgetContextMenuTrigger"
          #contextMenuTriggerElement="matMenuTrigger"
          class="context-menu-trigger"
        ></div>
      }
      @if (dashboard.locked) {
        @if (isFilterGroupParent) {
          <div
            [matMenuTriggerFor]="lockedWidgetContextMenuTrigger"
            #contextMenuTriggerElement="matMenuTrigger"
            class="context-menu-trigger"
          ></div>
        }
      }
      @if (canSort) {
        <div class="chart-sort-container--x">
          <span class="sort-label">{{ t('widgets.sort') }}</span>
          <xi-icon-button
            buttonSize="small"
            [disabled]="isSortDisabled()"
            (onClick)="sortChartItems(horizontalAxisField)"
            [icon]="getSortIcon(horizontalAxisField)"
            [tooltip]="{ text: horizontalAxisSortTooltip, direction: 8 }"
          >
          </xi-icon-button>
          <xi-icon-button
            buttonSize="small"
            [disabled]="isSortDisabled()"
            [icon]="widget.payload.sort?.field === 'custom' ? 'edit_filled' : 'edit'"
            [tooltip]="{ text: customSortTooltip, direction: 8 }"
            (onClick)="enableCustomSort()"
          ></xi-icon-button>
        </div>
      }
    </div>

    @if (isDataSourceNotFound) {
      <div class="text-center">
        <h1>{{ t('widgets.data-source-removed-select-another') }}</h1>
      </div>
    } @else {
      @if (noDocuments || noData || hasInvalidComparison || noPermission) {
        <div class="text-center">
          @if (hasInvalidComparison) {
            <h1>{{ t('widgets.has-invalid-comparison') }}</h1>
          } @else {
            @if (noPermission) {
              <h1>
                {{ t('widgets.no-permission-to-view') }}
              </h1>
            } @else {
              <h1>
                @if (noDocuments) {
                  {{ t('widgets.no-documents-found') }}
                }
                @if (!noDocuments && noData) {
                  {{ t('widgets.no-data-found') }}
                }
              </h1>
            }
          }
          @if (isDrilledDown && !topicPhraseCloud) {
            <xi-default-button
              class="no-data-back-button"
              fill="outline"
              [text]="t('widgets.go-back-from-drill-down')"
              iconLeft="arrow_left"
              buttonSize="small"
              (onClick)="drilldownInto('none', false)"
            ></xi-default-button>
          }
          @if (isZoomedIn) {
            <button class="reset-zoom-button" (click)="resetZoom()">
              <mat-icon>zoom_out</mat-icon> {{ t('widgets.reset-zoom') }}
            </button>
          }
        </div>
      }
    }
    @if (isShowAsDataTable) {
      <div
        class="content data_table"
        [ngClass]="{
          hide:
            noData || isDataSourceNotFound || hasInvalidComparison || noPermission || noDocuments,
        }"
      >
        <table mat-table [dataSource]="dataSource">
          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.name') }}</th>
            <td mat-cell *matCellDef="let element">
              @if (isAnalyticItemOfCustomType('date')) {
                {{ element.name | lexDate }}
              } @else {
                {{ element.name || '&lt;blank&gt;' }}
              }
            </td>
          </ng-container>
          <ng-container matColumnDef="minute">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.date') }}</th>
            <td mat-cell *matCellDef="let element">{{ element.minute }}</td>
          </ng-container>
          <ng-container matColumnDef="hour">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.date') }}</th>
            <td mat-cell *matCellDef="let element">{{ element.hour }}</td>
          </ng-container>
          <ng-container matColumnDef="day">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.date') }}</th>
            <td mat-cell *matCellDef="let element">{{ element.day }}</td>
          </ng-container>
          <ng-container matColumnDef="week">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.week') }}</th>
            <td mat-cell *matCellDef="let element">{{ element.week }}</td>
          </ng-container>
          <ng-container matColumnDef="month">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.month') }}</th>
            <td mat-cell *matCellDef="let element">{{ element.month }}</td>
          </ng-container>
          <ng-container matColumnDef="quarter">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.quarter') }}</th>
            <td mat-cell *matCellDef="let element">{{ element.quarter }}</td>
          </ng-container>
          <ng-container matColumnDef="year">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.year') }}</th>
            <td mat-cell *matCellDef="let element">{{ element.year }}</td>
          </ng-container>
          <ng-container matColumnDef="count">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.count') }}</th>
            <td mat-cell *matCellDef="let element">{{ element.count | number }}</td>
          </ng-container>
          <ng-container matColumnDef="total">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.volume') }}</th>
            <td mat-cell *matCellDef="let element">{{ element.total | number }}</td>
          </ng-container>
          <ng-container matColumnDef="sentiment">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.avg-sentiment') }}</th>
            <td mat-cell *matCellDef="let element">{{ element.sentiment | number: '.2' }}</td>
          </ng-container>
          <ng-container matColumnDef="negative">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.negative') }}</th>
            <td mat-cell *matCellDef="let element">{{ element.negative | number }}</td>
          </ng-container>
          <ng-container matColumnDef="neutral">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.neutral') }}</th>
            <td mat-cell *matCellDef="let element">{{ element.neutral | number }}</td>
          </ng-container>
          <ng-container matColumnDef="positive">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.positive') }}</th>
            <td mat-cell *matCellDef="let element">{{ element.positive | number }}</td>
          </ng-container>
          <ng-container matColumnDef="very_negative">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.very-negative') }}</th>
            <td mat-cell *matCellDef="let element">{{ element.very_negative | number }}</td>
          </ng-container>
          <ng-container matColumnDef="somewhat_negative">
            <th mat-header-cell *matHeaderCellDef>
              {{ t('widgets.datatable.somewhat-negative') }}
            </th>
            <td mat-cell *matCellDef="let element">{{ element.somewhat_negative | number }}</td>
          </ng-container>
          <ng-container matColumnDef="somewhat_positive">
            <th mat-header-cell *matHeaderCellDef>
              {{ t('widgets.datatable.somewhat-positive') }}
            </th>
            <td mat-cell *matCellDef="let element">{{ element.somewhat_positive | number }}</td>
          </ng-container>
          <ng-container matColumnDef="very_positive">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.very-positive') }}</th>
            <td mat-cell *matCellDef="let element">{{ element.very_positive | number }}</td>
          </ng-container>
          <ng-container matColumnDef="negative_pct">
            <th mat-header-cell *matHeaderCellDef>
              {{ t('widgets.datatable.negative-percentage') }}
            </th>
            <td mat-cell *matCellDef="let element">
              {{ element.negative_pct | percent: '1.1-1' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="neutral_pct">
            <th mat-header-cell *matHeaderCellDef>
              {{ t('widgets.datatable.neutral-percentage') }}
            </th>
            <td mat-cell *matCellDef="let element">{{ element.neutral_pct | percent: '1.1-1' }}</td>
          </ng-container>
          <ng-container matColumnDef="positive_pct">
            <th mat-header-cell *matHeaderCellDef>
              {{ t('widgets.datatable.positive-percentage') }}
            </th>
            <td mat-cell *matCellDef="let element">
              {{ element.positive_pct | percent: '1.1-1' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="very_negative_pct">
            <th mat-header-cell *matHeaderCellDef>
              {{ t('widgets.datatable.very-negative-percentage') }}
            </th>
            <td mat-cell *matCellDef="let element">
              {{ element.very_negative_pct | percent: '1.1-1' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="somewhat_negative_pct">
            <th mat-header-cell *matHeaderCellDef>
              {{ t('widgets.datatable.somewhat-negative-percentage') }}
            </th>
            <td mat-cell *matCellDef="let element">
              {{ element.somewhat_negative_pct | percent: '1.1-1' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="somewhat_positive_pct">
            <th mat-header-cell *matHeaderCellDef>
              {{ t('widgets.datatable.somewhat-positive-percentage') }}
            </th>
            <td mat-cell *matCellDef="let element">
              {{ element.somewhat_positive_pct | percent: '1.1-1' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="very_positive_pct">
            <th mat-header-cell *matHeaderCellDef>
              {{ t('widgets.datatable.very-positive-percentage') }}
            </th>
            <td mat-cell *matCellDef="let element">
              {{ element.very_positive_pct | percent: '1.1-1' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="xAxis">
            <th mat-header-cell *matHeaderCellDef>
              {{ getScatterPlotColumnHeader(this.widget.payload.xAxis, 'current') }}
            </th>
            <td mat-cell *matCellDef="let element">
              {{ element.xAxis | number: '1.0-2' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="xAxisPrev">
            <th mat-header-cell *matHeaderCellDef>
              {{ getScatterPlotColumnHeader(this.widget.payload.xAxis, 'previous') }}
            </th>
            <td mat-cell *matCellDef="let element">
              {{ element.xAxisPrev | number: '1.0-2' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="yAxis">
            <th mat-header-cell *matHeaderCellDef>
              {{ getScatterPlotColumnHeader(this.widget.payload.yAxis, 'current') }}
            </th>
            <td mat-cell *matCellDef="let element">
              {{ element.yAxis | number: '1.0-2' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="yAxisPrev">
            <th mat-header-cell *matHeaderCellDef>
              {{ getScatterPlotColumnHeader(this.widget.payload.yAxis, 'previous') }}
            </th>
            <td mat-cell *matCellDef="let element">
              {{ element.yAxisPrev | number: '1.0-2' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="count_previous">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.count-previous') }}</th>
            <td mat-cell *matCellDef="let element">
              {{ element.previousCount | number: '1.0-2' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="count_current">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.count-current') }}</th>
            <td mat-cell *matCellDef="let element">
              {{ element.currentCount | number: '1.0-2' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="count_previous_pct">
            <th mat-header-cell *matHeaderCellDef>
              {{ t('widgets.datatable.percentage-previous') }}
            </th>
            <td mat-cell *matCellDef="let element">
              {{ element.previousPercentage | percent: '1.1-1' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="count_current_pct">
            <th mat-header-cell *matHeaderCellDef>
              {{ t('widgets.datatable.percentage-previous') }}
            </th>
            <td mat-cell *matCellDef="let element">
              {{ element.currentPercentage | percent: '1.1-1' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="count_pct">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.percentage') }}</th>
            <td mat-cell *matCellDef="let element">
              {{ element.percentage | percent: '1.1-1' }}
            </td>
          </ng-container>
          @if (comparison?.filters.length) {
            @for (filter of comparison.filters; track filter; let i = $index) {
              <ng-container [matColumnDef]="'count_bucket_' + i">
                <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.count') }}</th>
                <td mat-cell *matCellDef="let element">
                  {{ element.buckets[i]?.count ?? 0 | number }}
                </td>
              </ng-container>
            }
            @for (filter of comparison.filters; track filter; let i = $index) {
              <ng-container [matColumnDef]="'count_bucket_' + i + '_pct'">
                <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.percentage') }}</th>
                <td mat-cell *matCellDef="let element">
                  {{ element.buckets[i]?.percentage ?? 0 | percent: '1.1-1' }}
                </td>
              </ng-container>
            }
            @for (filter of comparison.filters; track filter; let i = $index) {
              <ng-container [matColumnDef]="'score_bucket_' + i">
                <th mat-header-cell *matHeaderCellDef>
                  {{ t('widgets.datatable.score') }}
                </th>
                <td mat-cell *matCellDef="let element">
                  {{ element.buckets[i]?.score ?? 0 | number: '1.0-2' }}
                </td>
              </ng-container>
            }
          }
          <ng-container matColumnDef="metadata">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.metadata') }}</th>
            <td mat-cell *matCellDef="let element">
              {{ element.metadata }}
            </td>
          </ng-container>
          <ng-container matColumnDef="score">
            <th mat-header-cell *matHeaderCellDef>
              {{ dataTableColumnHeaders['score'] || t('widgets.datatable.score') }}
            </th>
            <td mat-cell *matCellDef="let element">
              {{ element.score | number: '1.0-2' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="secondary_score">
            <th mat-header-cell *matHeaderCellDef>
              {{ dataTableColumnHeaders['secondary_score'] || t('widgets.datatable.score') }}
            </th>
            <td mat-cell *matCellDef="let element">
              {{ element.secondary_score | number: '1.0-2' }}
            </td>
          </ng-container>

          @if (
            widget.payload.type === 'breakdown' &&
            widget.payload.breakdownOptions.scoring === 'bucket'
          ) {
            @for (
              bucket of widget.payload.breakdownOptions.fieldBuckets ?? [];
              track bucket;
              let bucketIndex = $index
            ) {
              <ng-container [matColumnDef]="'bucket_' + bucketIndex">
                <th mat-header-cell *matHeaderCellDef>{{ bucket.bucketName }}</th>
                <td mat-cell *matCellDef="let element">
                  @if (widget.payload.display?.showAsPercentage) {
                    {{ (element['bucket_' + bucketIndex] ?? 0) / 100 | percent: '1.1-1' }}
                  } @else {
                    {{ element['bucket_' + bucketIndex] | number }}
                  }
                </td>
              </ng-container>
            }
          }
          @if (
            widget.payload.type === 'breakdown' &&
            widget.payload.additionalBreakdownOptions?.scoring === 'bucket'
          ) {
            @for (
              bucket of widget.payload.additionalBreakdownOptions.fieldBuckets ?? [];
              track bucket;
              let bucketIndex = $index
            ) {
              <ng-container [matColumnDef]="'secondary_bucket_' + bucketIndex">
                <th mat-header-cell *matHeaderCellDef>{{ bucket.bucketName }}</th>
                <td mat-cell *matCellDef="let element">
                  @if (widget.payload.display?.showAsPercentage) {
                    {{ (element['secondary_bucket_' + bucketIndex] ?? 0) / 100 | percent: '1.1-1' }}
                  } @else {
                    {{ element['secondary_bucket_' + bucketIndex] | number }}
                  }
                </td>
              </ng-container>
            }
          }

          <ng-container matColumnDef="topScore">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.top') }}</th>
            <td mat-cell *matCellDef="let element">
              {{ element.topScore | number: '1.0-2' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="neutralScore">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.neutral') }}</th>
            <td mat-cell *matCellDef="let element">
              {{ element.neutralScore | number: '1.0-2' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="bottomScore">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.bottom') }}</th>
            <td mat-cell *matCellDef="let element">
              {{ element.bottomScore | number: '1.0-2' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="secondary_count">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.count') }}</th>
            <td mat-cell *matCellDef="let element">{{ element.secondary_count | number }}</td>
          </ng-container>
          <ng-container matColumnDef="secondary_topScore">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.top') }}</th>
            <td mat-cell *matCellDef="let element">
              {{ element.secondary_topScore | number: '1.0-2' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="secondary_neutralScore">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.neutral') }}</th>
            <td mat-cell *matCellDef="let element">
              {{ element.secondary_neutralScore | number: '1.0-2' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="secondary_bottomScore">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.bottom') }}</th>
            <td mat-cell *matCellDef="let element">
              {{ element.secondary_bottomScore | number: '1.0-2' }}
            </td>
          </ng-container>
          <ng-container matColumnDef="metric">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.metric') }}</th>
            <td mat-cell *matCellDef="let element">
              {{ element.metric }}
            </td>
          </ng-container>
          <ng-container matColumnDef="element">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.element') }}</th>
            <td mat-cell *matCellDef="let element">
              {{ element.element }}
            </td>
          </ng-container>
          <ng-container matColumnDef="method">
            <th mat-header-cell *matHeaderCellDef>{{ t('widgets.datatable.method') }}</th>
            <td mat-cell *matCellDef="let element">
              {{ element.method }}
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
        </table>
      </div>
    }
    @if (isCustomSortMode) {
      <div cdkScrollable class="widget-chart__custom-sort-container">
        <h5>{{ t('widgets.reorder-by-drag-drop') }}</h5>
        <mat-list
          cdkDropList
          (cdkDropListDropped)="onItemsReordered($event)"
          class="xi-draggable-list"
        >
          @for (item of customSortOrder; track item) {
            <mat-list-item cdkDrag cdkDragPreviewContainer="parent" class="xi-draggable-list-item">
              <div cdkDragHandle>
                <xi-icon icon="drag_handle"></xi-icon>
              </div>
              @switch (customType) {
                @case ('date') {
                  {{ transformLabel(item.label, 'date') }}
                }
                @case ('double') {
                  {{ transformLabel(item.label, 'double') }}
                }
                @case ('long') {
                  {{ transformLabel(item.label, 'long') }}
                }
                @default {
                  {{ item.label }}
                }
              }
            </mat-list-item>
          }
        </mat-list>
      </div>
    }
  </div>

  <ng-template #widgetContextMenuPointName>
    @if (pointName !== undefined) {
      <div
        mat-menu-item
        class="mat-menu-header"
        disabled
        matTooltip="{{ pointDisplayName }}"
        matTooltipPosition="above"
      >
        {{ pointDisplayName | truncateEnd: 30 }}
      </div>
    }
  </ng-template>

  <mat-menu #widgetContextMenuTrigger="matMenu" class="menu-with-xi-icons" xPosition="before">
    <ng-template matMenuContent>
      <ng-template [ngTemplateOutlet]="widgetContextMenuPointName"></ng-template>
      @if (showGoBackButton) {
        <button mat-menu-item (click)="goBackButtonClicked()">
          <xi-icon icon="arrow_left"></xi-icon>
          <div>{{ t('widgets.go-back') }}</div>
        </button>
      }
      @if (isDrilledDown && !topicPhraseCloud) {
        <button mat-menu-item (click)="drilldownInto('none', false)">
          <xi-icon icon="arrow_left"></xi-icon>
          <div>{{ t('widgets.go-back-from-drill-down') }}</div>
        </button>
      }
      @if (pointName !== undefined) {
        @if (canAddFilterToWidgetGroup) {
          <button
            mat-menu-item
            [disabled]="!hasDocFilterGroups"
            (click)="showAddToWidgetGroupModal = true"
          >
            <xi-icon icon="filter"></xi-icon>
            <div>{{ t('widgets.add-to-widget-group') }}</div>
          </button>
        }
        <ng-template [ngTemplateOutlet]="parentFilterMenu"></ng-template>
        <div
          [matTooltip]="
            !pointHasDocuments
              ? widgetContextMenu?.series?.name !== 'Total volume'
                ? t('widgets.show-documents-no-documents')
                : t('widgets.show-documents-not-available-total-volume')
              : null
          "
        >
          <button mat-menu-item [disabled]="!pointHasDocuments" (click)="showDocumentsForItem()">
            <xi-icon [color]="!pointHasDocuments ? 'disabled' : 'orca'" icon="preview"></xi-icon>
            <div>{{ t('widgets.show-documents') }}</div>
          </button>
        </div>
        @if (!isReadOnly) {
          <button
            mat-menu-item
            [disabled]="!pointHasDocuments"
            [matMenuTriggerFor]="collectionsMenuTrigger"
          >
            <xi-icon
              [color]="!pointHasDocuments ? 'disabled' : 'orca'"
              icon="collections"
            ></xi-icon>
            <div>{{ t('widgets.collections') }}</div>
          </button>
        }
        <mat-menu #collectionsMenuTrigger class="menu-with-xi-icons" xPosition="after">
          <button mat-menu-item (click)="openSaveDocumentsToCollection('NEW')">
            <xi-icon icon="document"></xi-icon>
            <div>{{ t('widgets.new-collection') }}</div>
          </button>
          <button mat-menu-item (click)="openSaveDocumentsToCollection('EXISTING')">
            <xi-icon icon="add_circle"></xi-icon>
            <div>{{ t('widgets.add-to-collection') }}</div>
          </button>
        </mat-menu>
        <div [matTooltip]="showDrilldownTooltip()">
          @if (canDrilldown) {
            <button mat-menu-item [disabled]="!hasOccurences()" (click)="drilldown()">
              <xi-icon [color]="!hasOccurences() ? 'disabled' : 'orca'" icon="visibility"></xi-icon>
              <div>{{ t('widgets.explore') }}</div>
            </button>
          }
        </div>
        <div [matTooltip]="showMentionsToolTip()">
          @if (!isDrilledDown && canDrilldownMentions) {
            <button
              mat-menu-item
              (click)="drilldownInto('mentions', true)"
              [disabled]="!hasMentions()"
            >
              <xi-icon [color]="!hasMentions() ? 'disabled' : 'orca'" icon="visibility"></xi-icon>
              <div>{{ t('widgets.show-mentions-for-item') }}</div>
            </button>
          }
        </div>
        <div [matTooltip]="showTopicPhrasesTooltip()">
          @if (!isDrilledDown && canDrilldownMentions && showTopicPhraseOption()) {
            <button
              mat-menu-item
              (click)="drilldownInto('topic_phrases', true)"
              [disabled]="!hasTopicPhrases() || isTopicOnlyEie()"
            >
              <xi-icon
                [color]="!hasTopicPhrases() || isTopicOnlyEie() ? 'disabled' : 'orca'"
                icon="visibility"
              ></xi-icon>
              <div>{{ t('widgets.show-phrases-for-topic') }}</div>
            </button>
          }
        </div>
        <div [matTooltip]="showEntityPhrasesTooltip()">
          @if (!isDrilledDown && canDrilldownMentions && showEntityPhraseOption()) {
            <button
              mat-menu-item
              (click)="drilldownInto('entity_phrases', true)"
              [disabled]="!hasEntityPhrases()"
            >
              <xi-icon
                [color]="!hasEntityPhrases() ? 'disabled' : 'orca'"
                icon="visibility"
              ></xi-icon>
              <div>{{ t('widgets.show-phrases-for-entity') }}</div>
            </button>
          }
        </div>
        <div [matTooltip]="showTopicPhraseWordCloudToolTip()">
          @if (!isDrilledDown && showTopicPhrasePolarityOption()) {
            <button
              mat-menu-item
              (click)="turnPolarityPointIntoPhraseWordCloud()"
              [disabled]="!hasTopicPhrases()"
            >
              <xi-icon
                [color]="!hasTopicPhrases() ? 'disabled' : 'orca'"
                icon="visibility"
              ></xi-icon>
              <div>{{ t('widgets.make-topic-phrase-wordcloud') }}</div>
            </button>
          }
        </div>
        <div [matTooltip]="showTopicThemesTooltip()">
          @if (!isDrilledDown && canDrilldownThemes) {
            <button
              mat-menu-item
              (click)="drilldownInto('themes', true)"
              [disabled]="isTopicOnlyEie()"
            >
              <xi-icon [color]="isTopicOnlyEie() ? 'disabled' : 'orca'" icon="visibility"></xi-icon>
              <div>{{ t('widgets.show-themes-for-item') }}</div>
            </button>
          }
        </div>
        <div [matTooltip]="showHideItemToolTip()">
          @if (!isDrilledDown && canHideItems) {
            <button mat-menu-item (click)="hideItem()" [disabled]="!enableHideItems()">
              <xi-icon
                [color]="!enableHideItems() ? 'disabled' : 'orca'"
                icon="remove_circle"
              ></xi-icon>
              <div>{{ hideItemLabel }}</div>
            </button>
          }
        </div>
        @if (isSmartSummaryEnabled && !isReadOnly) {
          <div>
            <div [matTooltip]="interactiveSmartSummaryMenu.tooltip">
              <button
                mat-menu-item
                (click)="openSmartSummary()"
                [disabled]="!interactiveSmartSummaryMenu.enabled"
              >
                <xi-icon
                  [color]="!interactiveSmartSummaryMenu.enabled ? 'disabled' : 'orca'"
                  icon="lightbulb"
                ></xi-icon>
                <div>{{ interactiveSmartSummaryMenu.label }}</div>
              </button>
            </div>
          </div>
        }
        @if (isSmartSummaryEnabled && !isReadOnly && pointSummaries?.length > 0) {
          <div [matTooltip]="removeInteractiveSmartSummaryMenu.tooltip">
            <button
              mat-menu-item
              (click)="removeSmartSummary()"
              [disabled]="!removeInteractiveSmartSummaryMenu.enabled"
            >
              <xi-icon
                [color]="!removeInteractiveSmartSummaryMenu.enabled ? 'disabled' : 'orca'"
                icon="indeterminate"
              ></xi-icon>
              <div>{{ removeInteractiveSmartSummaryMenu.label }}</div>
            </button>
          </div>
        }
        @if (!pointIsTotalVolume && isSmartSummaryEnabled && !isReadOnly) {
          <div [matTooltip]="downloadSmartSummaryRequestToolTip">
            <button
              mat-menu-item
              (click)="downloadSmartSummarySpotERequest()"
              [disabled]="downloadingSpoteRequest || !pointHasDocuments || !canCreateSmartSummary"
            >
              <xi-icon
                [color]="
                  downloadingSpoteRequest || !pointHasDocuments || !canCreateSmartSummary
                    ? 'disabled'
                    : 'orca'
                "
                icon="download"
              ></xi-icon>
              <div>{{ t('widgets.smart-summary.download-smart-summary-spote-request') }}</div>
            </button>
          </div>
        }
        @if (canAddComment) {
          <div [matTooltip]="commentDialogTooltip()">
            <button
              mat-menu-item
              [disabled]="!canOpenCommentDialog"
              [matMenuTriggerFor]="commentsMenuTrigger"
            >
              <xi-icon
                [color]="!canOpenCommentDialog ? 'disabled' : 'orca'"
                icon="comment"
              ></xi-icon>
              <div>{{ t('widgets.comments.comments') }}</div>
            </button>

            <mat-menu #commentsMenuTrigger class="menu-with-xi-icons" xPosition="after">
              <div [matTooltip]="addCommentDialogToolTip()">
                <button
                  mat-menu-item
                  [disabled]="isReadOnly"
                  (click)="openCommentDialog(point, false)"
                >
                  <xi-icon icon="add_circle"></xi-icon>
                  <div>{{ t('widgets.comments.label-add') }}</div>
                </button>
              </div>
              <div [matTooltip]="openCommentDialogToolTip(point)" [matTooltipPosition]="'left'">
                <button
                  mat-menu-item
                  [disabled]="!hasComment(point)"
                  (click)="onCommentListOpenClicked(point)"
                >
                  <xi-icon icon="app_explore"></xi-icon>
                  <div>{{ t('widgets.comments.label-open') }}</div>
                </button>
              </div>
            </mat-menu>
          </div>
        }
        @if (canChangeItemColor) {
          <div>
            @if (!isDrilledDown) {
              <button mat-menu-item (click)="changeItemColor()" [disabled]="isChangeColorDisabled">
                <xi-icon icon="theme"></xi-icon>
                <div>{{ t('widgets.set-color') }}</div>
              </button>
              <button mat-menu-item (click)="resetPointColor()" [disabled]="!hasPointColorOverride">
                <xi-icon
                  icon="theme_filled"
                  [color]="!hasPointColorOverride ? 'disabled' : 'orca'"
                ></xi-icon>
                <div>{{ t('widgets.reset-color') }}</div>
              </button>
            }
          </div>
        }
      }
    </ng-template>
  </mat-menu>

  <mat-menu #lockedWidgetContextMenuTrigger="matMenu" class="menu-with-xi-icons" xPosition="before">
    <ng-template [ngTemplateOutlet]="widgetContextMenuPointName"></ng-template>
    <ng-template [ngTemplateOutlet]="parentFilterMenu"></ng-template>
  </mat-menu>

  <ng-template #parentFilterMenu>
    @if (isFilterGroupParent && pointName !== undefined) {
      @if (!point.selected && filterGroupFilterDrilledDownCheck) {
        <button
          mat-menu-item
          [disabled]="!canAddAsParentFilter"
          (click)="addDocFilterGroupBluePill()"
        >
          <xi-icon icon="filter"></xi-icon>
          <div>{{ t('widgets.set-as-parent-filter') }}</div>
        </button>
      }
      @if (point.selected) {
        <button
          mat-menu-item
          [disabled]="!hasDocFilterGroups"
          (click)="removeDocFilterGroupBluePill()"
        >
          <xi-icon icon="filter"></xi-icon>
          <div>{{ t('widgets.unset-parent-filter') }}</div>
        </button>
      }
    }
  </ng-template>

  @if (isCustomSortMode) {
    <div class="widget-chart__sort-save-bar">
      <div class="widget-chart__sort-save-bar-actions">
        <xi-default-button
          [text]="t('widgets.cancel')"
          fill="text"
          (onClick)="finishCustomSort()"
        ></xi-default-button>
        <xi-default-button
          [text]="t('widgets.save')"
          (onClick)="finishCustomSort(true)"
        ></xi-default-button>
      </div>
    </div>
  }
  @if (showAddToWidgetGroupModal) {
    <app-lex-xi-modal
      [isVisible]="showAddToWidgetGroupModal"
      maxWidth="720px"
      (close)="onAddFiltersToWidgetGroup(undefined)"
    >
      <ng-template appXIModalContent>
        @if (showAddToWidgetGroupModal) {
          <app-add-to-widget-group-dialog
            [dashboardViewComponent]="dashboardViewComponent"
            [widget]="widget"
            (modalClosed)="onAddFiltersToWidgetGroup($event)"
          ></app-add-to-widget-group-dialog>
        }
      </ng-template>
    </app-lex-xi-modal>
  }
  @if (showDocumentViewModal) {
    <app-lex-xi-modal
      [isVisible]="showDocumentViewModal"
      [canBackdropClose]="false"
      maxWidth="80vw"
      (close)="onDocumentViewModalClosed($event)"
    >
      <ng-template appXIModalContent>
        @if (showDocumentViewModal) {
          <app-document-view-dialog
            [options]="documentViewModalOptions"
            (modalClosed)="onDocumentViewModalClosed($event)"
          ></app-document-view-dialog>
        }
      </ng-template>
    </app-lex-xi-modal>
  }
  @if (showSmartSummaryModal) {
    <app-lex-xi-modal
      [isVisible]="showSmartSummaryModal"
      [canBackdropClose]="false"
      maxWidth="80vw"
      (close)="onSmartSummaryClosed($event)"
    >
      <ng-template appXIModalContent>
        @if (showSmartSummaryModal) {
          <app-interactive-smart-summary
            [options]="interactiveSmartSummaryOptions"
            (modalClosed)="onSmartSummaryClosed($event)"
            (addComment)="onAddSmartSummaryComment($event)"
            (addNotes)="onAddSmartSummaryNotes($event)"
          ></app-interactive-smart-summary>
        }
      </ng-template>
    </app-lex-xi-modal>
  }
</ng-container>
